import { useEffect, useState } from "react";
import { Lightbulb, Bug, Send, Coins, Clock, CheckCircle2, XCircle } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const MAX_CHARS = 500;

export default function Recomendaciones() {
    const { refreshMe } = useAuth();
    const [type, setType] = useState("recommendation");
    const [content, setContent] = useState("");
    const [history, setHistory] = useState([]);
    const [sending, setSending] = useState(false);
    const [limits, setLimits] = useState({ can_submit: true, next_allowed_at: null });

    async function load() {
        const [r, l] = await Promise.all([
            api.get("/recommendations"),
            api.get("/recommendations/limits"),
        ]);
        setHistory(r.data || []);
        setLimits(l.data || { can_submit: true });
    }
    useEffect(() => {
        load();
    }, []);

    const remaining = MAX_CHARS - content.length;
    const isOver = remaining < 0;

    async function submit(e) {
        e.preventDefault();
        if (!content.trim() || isOver) return;
        setSending(true);
        try {
            await api.post("/recommendations", { type, content });
            await refreshMe();
            await load();
            setContent("");
            toast.success(
                "¡Recomendación enviada! Quedó en estado Pendiente, será revisada por el ejecutivo.",
                { duration: 5000 }
            );
        } catch (e2) {
            toast.error(e2.response?.data?.detail || "Error al enviar");
        } finally {
            setSending(false);
        }
    }

    const nextDate = limits.next_allowed_at ? new Date(limits.next_allowed_at) : null;

    return (
        <div className="p-6 md:p-10 max-w-3xl mx-auto fade-in" data-testid="recomendaciones-page">
            <h1 className="text-3xl font-semibold mb-1">Recomendaciones</h1>
            <p className="text-edu-body mb-6">
                Comparte tus ideas o reporta problemas. Cada envío aprobado por el ejecutivo te otorga{" "}
                <strong>10 monedas</strong>. Máximo <strong>1 envío por semana</strong>.
            </p>

            {!limits.can_submit && nextDate && (
                <div
                    className="edu-card p-4 mb-5 inline-flex items-start gap-3 border-l-4 border-l-edu-gold"
                    data-testid="weekly-limit-banner"
                >
                    <Clock size={18} className="text-edu-gold mt-0.5" />
                    <div>
                        <p className="text-sm font-medium">Ya enviaste tu recomendación de esta semana.</p>
                        <p className="text-xs text-edu-muted mt-0.5">
                            Podrás enviar otra a partir del {nextDate.toLocaleDateString("es-PE", { day: "numeric", month: "long" })}.
                        </p>
                    </div>
                </div>
            )}

            <form onSubmit={submit} className="edu-card p-6 mb-6 space-y-4" data-testid="recommendation-form">
                <div>
                    <label className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-2 block">
                        Tipo
                    </label>
                    <div className="flex gap-2">
                        <TypeBtn
                            active={type === "recommendation"}
                            onClick={() => setType("recommendation")}
                            icon={Lightbulb}
                            label="Recomendación"
                            testId="type-recommendation"
                        />
                        <TypeBtn
                            active={type === "bug"}
                            onClick={() => setType("bug")}
                            icon={Bug}
                            label="Reporte de Bug"
                            testId="type-bug"
                        />
                    </div>
                </div>
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="text-xs uppercase tracking-widest text-edu-muted font-semibold">
                            Mensaje
                        </label>
                        <span
                            className={`text-xs font-medium ${isOver ? "text-red-600" : remaining < 50 ? "text-yellow-700" : "text-edu-muted"}`}
                            data-testid="char-counter"
                        >
                            {content.length} / {MAX_CHARS} caracteres
                        </span>
                    </div>
                    <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value.slice(0, MAX_CHARS + 50))}
                        rows={4}
                        className="edu-input w-full"
                        placeholder="Cuéntanos tu idea o describe el problema (máximo 500 caracteres)…"
                        data-testid="recommendation-content"
                        required
                        disabled={!limits.can_submit}
                        maxLength={MAX_CHARS + 50}
                    />
                </div>
                <button
                    type="submit"
                    disabled={sending || !limits.can_submit || isOver || !content.trim()}
                    className="edu-btn-primary px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="recommendation-submit"
                >
                    <Send size={14} /> Enviar (pendiente de aprobación)
                </button>
            </form>

            <div>
                <h3 className="text-lg font-semibold mb-3">Mi historial</h3>
                <div className="space-y-3">
                    {history.length === 0 && <p className="text-sm text-edu-muted">Sin envíos previos.</p>}
                    {history.map((h) => (
                        <div key={h.id} className="edu-card p-4">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <span className="edu-pill bg-edu-navy/10 text-edu-navy capitalize">
                                        {h.type === "bug" ? "Bug" : "Recomendación"}
                                    </span>
                                    <StatusPill status={h.status} />
                                </div>
                                <span className="text-xs text-edu-muted">{(h.created_at || "").slice(0, 10)}</span>
                            </div>
                            <p className="text-sm text-edu-body whitespace-pre-line">{h.content}</p>
                            {h.status === "approved" && (
                                <p className="text-xs text-emerald-700 mt-2 inline-flex items-center gap-1">
                                    <Coins size={12} /> +{h.reward} monedas otorgadas
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        pending: { label: "Pendiente", cls: "bg-yellow-100 text-yellow-800", Icon: Clock },
        approved: { label: "Aprobada", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
        rejected: { label: "Rechazada", cls: "bg-red-100 text-red-700", Icon: XCircle },
    };
    const m = map[status] || map.pending;
    const Icon = m.Icon;
    return (
        <span className={`edu-pill ${m.cls} inline-flex items-center gap-1`}>
            <Icon size={11} /> {m.label}
        </span>
    );
}

function TypeBtn({ active, onClick, icon: Icon, label, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm transition ${
                active ? "border-edu-navy bg-edu-navy text-white" : "border-edu hover:bg-gray-50"
            }`}
        >
            <Icon size={14} /> {label}
        </button>
    );
}
